import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  CreditCard, Zap, CheckCircle, Package, TrendingUp, 
  Calendar, Download, Plus, Crown, Sparkles, ArrowRight
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const Billing = () => {
  const [showUpgrade, setShowUpgrade] = useState(false);

  const currentPlan = {
    name: 'Professional',
    price: 299,
    billingCycle: 'monthly',
    nextBilling: '2025-01-15',
    features: [
      '50 BOM analyses per month',
      'Up to 10 team members',
      'AI-powered recommendations',
      'Email support',
      'Basic API access'
    ]
  };

  const credits = {
    used: 32,
    total: 50,
    percentage: 64
  };

  const usage = [
    { name: 'BOM Analyses', used: 32, limit: 50 },
    { name: 'AI Queries', used: 156, limit: 500 },
    { name: 'API Calls', used: 2340, limit: 10000 },
    { name: 'Export Reports', used: 12, limit: 100 }
  ];

  const plans = [
    {
      name: 'Starter',
      price: 99,
      popular: false,
      features: [
        '10 BOM analyses per month',
        '3 team members',
        'Basic alternates',
        'Email support'
      ]
    },
    {
      name: 'Professional',
      price: 299,
      popular: true,
      features: [
        '50 BOM analyses per month',
        '10 team members',
        'AI-powered recommendations',
        'Priority email support',
        'API access'
      ]
    },
    {
      name: 'Enterprise',
      price: 999,
      popular: false,
      features: [
        'Unlimited BOM analyses',
        'Unlimited team members',
        'Advanced AI features',
        'Dedicated support',
        'Custom integrations',
        'SLA guarantee'
      ]
    }
  ];

  const invoices = [
    { id: 'INV-2024-012', date: '2024-12-15', amount: 299, status: 'paid' },
    { id: 'INV-2024-011', date: '2024-11-15', amount: 299, status: 'paid' },
    { id: 'INV-2024-010', date: '2024-10-15', amount: 299, status: 'paid' },
  ];

  const handleAddCredits = () => {
    toast.success('Credits added successfully');
  };

  return (
    <Layout title="Credits & Plans" subtitle="Manage your subscription and usage">
      <div className="space-y-6" data-testid="billing-page">
        {/* Current Plan & Credits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Plan */}
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-green-800 flex items-center gap-2">
                  <Crown className="h-5 w-5" />
                  Current Plan
                </CardTitle>
                <Badge className="bg-green-600 text-white">{currentPlan.name}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <p className="text-4xl font-bold text-green-700">${currentPlan.price}</p>
                <p className="text-green-600">per {currentPlan.billingCycle}</p>
              </div>
              <ul className="space-y-2 mb-6">
                {currentPlan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-green-800">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between text-sm text-green-700">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Next billing: {currentPlan.nextBilling}
                </span>
                <Button 
                  variant="outline" 
                  className="border-green-600 text-green-700"
                  onClick={() => setShowUpgrade(true)}
                >
                  Upgrade Plan
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Credits */}
          <Card className="bg-white border-gray-200 shadow-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-500" />
                  Analysis Credits
                </CardTitle>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={handleAddCredits}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Credits
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <p className="text-5xl font-bold text-gray-900">{credits.total - credits.used}</p>
                <p className="text-gray-500">credits remaining</p>
              </div>
              <Progress value={credits.percentage} className="h-3 mb-2" />
              <p className="text-sm text-gray-500 text-center">
                {credits.used} of {credits.total} credits used this month
              </p>
              
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <Sparkles className="h-4 w-4 inline mr-1" />
                  Running low? Upgrade to Professional for 50 credits/month
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Usage Breakdown */}
        <Card className="bg-white border-gray-200 shadow-md">
          <CardHeader>
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Usage This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {usage.map((item, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{item.name}</span>
                    <span className="text-sm text-gray-500">{item.used}/{item.limit}</span>
                  </div>
                  <Progress value={(item.used / item.limit) * 100} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Plan Comparison */}
        <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Choose Your Plan</DialogTitle>
              <DialogDescription>
                Select the plan that best fits your needs
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-4">
              {plans.map((plan) => (
                <div 
                  key={plan.name}
                  className={`p-6 rounded-xl border-2 ${
                    plan.popular 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {plan.popular && (
                    <Badge className="bg-green-600 text-white mb-3">Most Popular</Badge>
                  )}
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    ${plan.price}<span className="text-sm font-normal text-gray-500">/mo</span>
                  </p>
                  <ul className="mt-4 space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full mt-6 ${
                      plan.popular 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                    disabled={plan.name === currentPlan.name}
                  >
                    {plan.name === currentPlan.name ? 'Current Plan' : 'Select Plan'}
                  </Button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Invoices */}
        <Card className="bg-white border-gray-200 shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-gray-600" />
                Billing History
              </CardTitle>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Download All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Package className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{invoice.id}</p>
                      <p className="text-sm text-gray-500">{invoice.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-gray-900">${invoice.amount}</span>
                    <Badge className="bg-green-100 text-green-800">{invoice.status}</Badge>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Billing;
